import { HTMLAttributes, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Logo from '@/components/base/Logo'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'
import { useTimeline } from '@/contexts/timeline'
import { useLayoutStore } from '@/store/layout'
import { useLoader } from '@/store/loader'
import { isLgScreen } from '@/utils/screen'

import { cn } from '@/lib/utils'

export type HeaderProps = HTMLAttributes<HTMLElement>

const Header = ({ className, ...props }: HeaderProps) => {
  const { strings } = useStore()
  const root = useRef<HTMLElement>(null!)
  const { context, contextSafe } = useGSAP({ scope: root })
  const needsMenu = useLayoutStore((state) => state.needsMenu)
  const homeLink = useRef<HTMLDivElement>(null!)
  const contactLink = useRef<HTMLDivElement>(null!)
  const timeline = useTimeline()
  const router = useRouter()

  const reveal = contextSafe(() => {
    console.log('header.reveal')

    const selector = context.selector || gsap.utils.selector(root.current)
    const logo = selector('[data-motion-logo]')[0]
    const panel = selector('[data-motion-panel]')[0]
    const button = selector('[data-motion-button]')[0]

    const timeline = gsap.timeline()

    if (!isLgScreen()) {
      timeline.fromTo(
        panel,
        { yPercent: -100 },
        { yPercent: 0, duration: 1.2, ease: 'power3.inOut' },
        0
      )
    }

    timeline
      .fromTo(
        logo,
        { yPercent: 110, autoAlpha: 0 },
        { yPercent: 0, autoAlpha: 1, duration: 1, ease: 'power3.inOut' },
        !isLgScreen() ? 0.2 : 0
      )
      .fromTo(
        button,
        { yPercent: 110, autoAlpha: 0 },
        { yPercent: 0, autoAlpha: 1, duration: 1, ease: 'power3.inOut' },
        !isLgScreen() ? 0.3 : 0.2
      )

    return timeline
  })

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useLoader.subscribe(
      (state) => state.compiled,
      (compiled) => {
        if (compiled) {
          unsubscribe()
          if (router.pathname === '/') {
            timeline.add(reveal(), 2.8)
          }
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <header ref={root} className={cn(['static', className])} {...props}>
      <span
        className="fixed left-0 top-0 z-header h-[6.1rem] w-full bg-black-dark md:hidden"
        data-motion-panel
      />

      <div className="fixed left-[4rem] top-spacing-sm z-logo overflow-hidden md:left-margin md:top-spacing-lg">
        <Link
          href="/"
          scroll={false}
          className="pointer-events-none relative -m-spacing-xxs flex p-spacing-xxs"
        >
          <span className="sr-only">8Bit</span>
          <Logo className="h-[1.3rem] w-[5.3rem] md:h-[1.7rem] md:w-[6.9rem]" data-motion-logo />
        </Link>
      </div>

      {needsMenu ? (
        <button
          className={cn([
            'fixed right-[4rem] top-[2.65rem] z-hamb h-[4.4rem] w-[4.4rem] -translate-y-[1.7rem] translate-x-[0.8rem] md:right-margin md:top-[4.7rem]',
            'before:absolute before:left-1/2 before:top-1/2 before:h-[.2rem] before:w-[2.8rem] before:-translate-x-1/2 before:-translate-y-[.5rem] before:bg-text-primary-dark',
            'after:absolute after:left-1/2 after:top-1/2 after:h-[.2rem] after:w-[2.8rem] after:-translate-x-1/2 after:translate-y-[.3rem] after:bg-text-primary-dark'
          ])}
        >
          <span className="sr-only">Menu</span>
        </button>
      ) : (
        <>
          <Transition
            in={router.pathname !== '/'}
            nodeRef={homeLink}
            timeout={{ enter: 100, exit: 700 }}
            unmountOnExit
          >
            {(state) => {
              console.log({ state })
              return (
                <div
                  ref={homeLink}
                  className="fixed right-[4rem] top-[2.5rem] z-hamb overflow-hidden md:right-margin md:top-[4.5rem]"
                >
                  <div className="relative -m-spacing-xxs flex p-spacing-xxs" data-motion-button>
                    <Link
                      href="/"
                      className={cn(
                        'relative -m-spacing-xxs flex p-spacing-xxs',
                        'transition-transform duration-700 ease-in-out-cubic',
                        {
                          'translate-y-full duration-0': state === 'entering',
                          'translate-y-0': state === 'entered',
                          '-translate-y-full': state === 'exiting' || state === 'exited'
                        }
                      )}
                    >
                      <Typography variant="regular">{strings?.backtohome}</Typography>
                    </Link>
                  </div>
                </div>
              )
            }}
          </Transition>

          <Transition
            in={router.pathname === '/'}
            nodeRef={contactLink}
            timeout={{ enter: 100, exit: 700 }}
            unmountOnExit
          >
            {(state) => (
              <div
                ref={contactLink}
                className="fixed right-[4rem] top-[2.5rem] z-hamb overflow-hidden md:right-margin md:top-[4.5rem]"
              >
                <div className="relative -m-spacing-xxs flex p-spacing-xxs" data-motion-button>
                  <Link
                    href="/contacts"
                    className={cn(
                      'relative -m-spacing-xxs flex p-spacing-xxs',
                      'transition-transform duration-700 ease-in-out-cubic',
                      {
                        'translate-y-full duration-0': state === 'entering',
                        'translate-y-0': state === 'entered',
                        '-translate-y-full': state === 'exiting' || state === 'exited'
                      }
                    )}
                  >
                    <Typography variant="regular">{strings?.contacts}</Typography>
                  </Link>
                </div>
              </div>
            )}
          </Transition>
        </>
      )}
    </header>
  )
}

export default Header
